import { getApiBaseUrl } from './apiConfig';

/**
 * 7일 무료 체험 사용 여부 조회 (GET /api/payment/subscription/trial-status)
 *
 * - 서버 인스턴스가 새로 뜨는 중이면 응답이 수십 초 늦을 수 있다. 버튼이 그동안 로딩으로 묶이지 않도록
 *   마지막으로 확인한 값을 uid별 localStorage에 두고 먼저 보여 준 뒤, 서버 값으로 갱신한다.
 * - 체험 시작 API가 서버에서 사용 여부를 다시 검사하므로, 캐시가 잠깐 낡아도 중복 체험은 생기지 않는다.
 */
export const TRIAL_STATUS_TIMEOUT_MS = 8 * 1000;
// 체험은 여러 화면·기기에서 시작될 수 있어 '미사용' 값은 낡을 수 있다. '사용함'은 되돌아가지 않으므로 기한이 없다.
export const TRIAL_UNUSED_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CACHE_KEY_PREFIX = 'wq_trial_used_';

export const loadCachedTrialUsed = (uid, now = Date.now()) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${uid}`);
    if (raw === 'true') return true;
    const match = /^false:(\d+)$/.exec(raw || '');
    if (match && now - Number(match[1]) < TRIAL_UNUSED_CACHE_TTL_MS) return false;
  } catch (_e) { /* localStorage 사용 불가 환경 무시 */ }
  return null;
};

export const saveCachedTrialUsed = (uid, trialUsed, now = Date.now()) => {
  if (!uid) return;
  try {
    localStorage.setItem(`${CACHE_KEY_PREFIX}${uid}`, trialUsed ? 'true' : `false:${now}`);
  } catch (_e) { /* localStorage 사용 불가 환경 무시 */ }
};

// 체험 시작 API의 거절 사유 중 '이미 사용함/결제 이력 있음'은 체험 버튼을 다시 보일 이유가 없는 상태다.
export const isTrialAlreadyUsedError = (message) => /이미/.test(String(message || ''));

/**
 * 서버에서 체험 사용 여부를 읽는다. 시간 안에 응답이 없거나 HTTP 오류면 예외를 던진다.
 * 성공하면 uid 캐시를 갱신한다.
 */
export const fetchTrialUsed = async (idToken, { uid, timeoutMs = TRIAL_STATUS_TIMEOUT_MS } = {}) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/payment/subscription/trial-status`, {
      headers: { Authorization: `Bearer ${idToken}` },
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!response.ok) throw new Error(`trial-status ${response.status}`);
    const data = await response.json();
    const trialUsed = Boolean(data?.trial_used);
    saveCachedTrialUsed(uid, trialUsed);
    return trialUsed;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};
